import React, { useEffect, useMemo, useRef, useState } from "react";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";
import CustomModal from "./customModal";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { useProfileAndSettings } from "../../../hooks/useProfileAndSettings";
import { getCurrencySymbol } from "../../../utils/currencyDisplay";
import usePagedShortProducts from "../../../hooks/usePagedShortProducts";

const getProductId = (productOrId) => {
  if (productOrId == null) return null;
  if (typeof productOrId === "object") {
    return (
      productOrId.menuItem_ID ??
      productOrId.MenuItem_ID ??
      productOrId.id ??
      null
    );
  }
  return productOrId;
};

const idsEqual = (a, b) => {
  if (a == null || b == null) return false;
  if (a == b) return true;
  if (parseInt(a, 10) === parseInt(b, 10)) return true;
  return String(a).trim() === String(b).trim();
};

const toShortProduct = (product) => {
  if (!product || typeof product !== "object") return null;
  const id = getProductId(product);
  if (id == null) return null;
  return {
    ...product,
    menuItem_ID: id,
    MenuItem_ID: id,
    menuItem_Name:
      product.menuItem_Name || product.MenuItem_Name || product.name || "",
    MenuItem_Name:
      product.MenuItem_Name || product.menuItem_Name || product.name || "",
    menuItem_Image:
      product.menuItem_Image || product.MenuItem_Image || "",
    MenuItem_Image:
      product.MenuItem_Image || product.menuItem_Image || "",
    menuItem_SalePrice:
      product.menuItem_SalePrice ?? product.MenuItem_SalePrice ?? 0,
    MenuItem_SalePrice:
      product.MenuItem_SalePrice ?? product.menuItem_SalePrice ?? 0,
    menuItem_HasVariant:
      product.menuItem_HasVariant ?? product.MenuItem_HasVariant ?? false,
    MenuItem_HasVariant:
      product.MenuItem_HasVariant ?? product.menuItem_HasVariant ?? false,
  };
};

export default function ProductMultiSelectionModal({
  showModal,
  setShowModal,
  showButton,
  onSubmit,
  setSelectedProducts,
  selectedProducts,
  products: legacyProducts,
  maxSelection = null,
  excludeIds = [],
  compID,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [tempSelectedIds, setTempSelectedIds] = useState([]);
  const productCacheRef = useRef(new Map());
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const { getCompCurrency } = useProfileAndSettings();
  const currencySymbol = getCurrencySymbol(getCompCurrency());

  const {
    products: pagedProducts,
    loading,
    loadingMore,
    hasMore,
    handleScroll,
  } = usePagedShortProducts({
    enabled: !!showModal,
    searchTerm,
    excludeIds,
    compID,
  });

  // Prefer self-fetched pages; fall back to legacy products prop during migration.
  const products = useMemo(() => {
    if (showModal) return pagedProducts;
    return Array.isArray(legacyProducts) ? legacyProducts : [];
  }, [showModal, pagedProducts, legacyProducts]);

  const seedCache = (list) => {
    (list || []).forEach((item) => {
      const short = toShortProduct(item);
      if (!short) return;
      productCacheRef.current.set(String(getProductId(short)), short);
    });
  };

  useEffect(() => {
    seedCache(products);
  }, [products]);

  useEffect(() => {
    if (!showModal) {
      setSearchTerm("");
      return;
    }

    const normalizedIds = (selectedProducts || [])
      .map((p) => getProductId(p))
      .filter((id) => id != null && String(id).trim() !== "");

    seedCache((selectedProducts || []).filter((p) => typeof p === "object"));
    setTempSelectedIds(normalizedIds);
  }, [selectedProducts, showModal]);

  const resolveSelectedProducts = () =>
    tempSelectedIds.map((id) => {
      const cached = productCacheRef.current.get(String(id));
      if (cached) return cached;
      return {
        menuItem_ID: id,
        MenuItem_ID: id,
        menuItem_Name: `Item ${id}`,
        MenuItem_Name: `Item ${id}`,
      };
    });

  const handleSubmit = () => {
    const resolved = resolveSelectedProducts();
    if (typeof onSubmit === "function") {
      onSubmit(resolved);
    }
    if (typeof setSelectedProducts === "function") {
      setSelectedProducts(resolved);
    }
    setShowModal(false);
  };

  const handleProductSelection = (product) => {
    const short = toShortProduct(product);
    if (!short) return;
    const productId = getProductId(short);
    productCacheRef.current.set(String(productId), short);

    setTempSelectedIds((prev) => {
      const index = prev.findIndex((p) => idsEqual(p, productId));
      if (index > -1) {
        const updated = [...prev];
        updated.splice(index, 1);
        return updated;
      }
      if (maxSelection === 1) return [productId];
      if (maxSelection && prev.length >= maxSelection) return prev;
      return [...prev, productId];
    });
  };

  const RenderProductCard = ({ product }) => {
    const productId = getProductId(product);
    const isSelected = tempSelectedIds.some((p) => idsEqual(p, productId));
    const name =
      product.menuItem_Name ||
      product.MenuItem_Name ||
      product.name ||
      "Unknown Product";
    const image =
      product.menuItem_Image ||
      product.MenuItem_Image ||
      "/assets/img/default-product.png";
    const price =
      product.menuItem_Price ||
      product.MenuItem_Price ||
      product.menuItem_SalePrice ||
      product.MenuItem_SalePrice;

    return (
      <div
        className="product-card"
        style={{
          background: isSelected
            ? isDarkMode
              ? `${themeColor}20`
              : `${themeColor}15`
            : isDarkMode
              ? "#2d3748"
              : "#ffffff",
          borderRadius: "12px",
          padding: "16px 20px",
          border: isSelected
            ? `2px solid ${themeColor}`
            : `2px solid ${isDarkMode ? "#4a5568" : "#e9ecef"}`,
          transition: "all 0.2s ease",
          cursor: "pointer",
          position: "relative",
          boxShadow: isSelected
            ? `0 4px 12px ${themeColor}40`
            : isDarkMode
              ? "0 2px 8px rgba(0, 0, 0, 0.3)"
              : "0 2px 8px rgba(0, 0, 0, 0.05)",
          marginBottom: "12px",
        }}
        onClick={() => handleProductSelection(product)}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div
            style={{
              width: "40px",
              height: "40px",
              background: isSelected
                ? themeColor
                : isDarkMode
                  ? "#4a5568"
                  : "#f8f9fa",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: "12px",
              border: `1px solid ${isDarkMode ? "#718096" : "#e9ecef"}`,
              overflow: "hidden",
            }}
          >
            <LazyLoadImage
              src={image}
              alt={name}
              style={{ height: "32px", width: "32px", objectFit: "contain" }}
            />
          </div>
          <div className="flex-grow-1">
            <span
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: isSelected
                  ? themeColor
                  : isDarkMode
                    ? "#e2e8f0"
                    : "#495057",
                display: "block",
                marginBottom: "2px",
              }}
            >
              {name}
            </span>
            {price ? (
              <small
                style={{
                  color: isSelected
                    ? themeColor
                    : isDarkMode
                      ? "#a0aec0"
                      : "#6c757d",
                  fontSize: "11px",
                  lineHeight: "1.3",
                }}
              >
                {currencySymbol}
                {price}
              </small>
            ) : null}
          </div>
        </div>
        <div
          style={{
            position: "absolute",
            top: "12px",
            right: "12px",
            width: "20px",
            height: "20px",
            borderRadius: "4px",
            border: isSelected
              ? `2px solid ${themeColor}`
              : `2px solid ${isDarkMode ? "#718096" : "#e9ecef"}`,
            background: isSelected ? themeColor : "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isSelected && (
            <i
              className="fa fa-check"
              style={{ fontSize: "10px", color: "white" }}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <CustomModal
      isOpen={showModal}
      title="Select Products"
      onClose={() => setShowModal(false)}
      onSubmit={handleSubmit}
      showButton={showButton}
      showSubmitButton={true}
      isCentered={true}
      submitButtonText="Select"
      themeColor={themeColor}
    >
      <p
        style={{
          color: isDarkMode ? "#a0aec0" : "#6c757d",
          marginBottom: "20px",
        }}
      >
        {maxSelection === 1
          ? "Select one product (single selection only)"
          : maxSelection
            ? `Select up to ${maxSelection} products`
            : "Select the products for the item"}
      </p>

      <div className="mb-4">
        <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
          <input
            type="text"
            className="form-control"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: "12px 16px",
              border: `2px solid ${isDarkMode ? "#4a5568" : "#e9ecef"}`,
              borderRadius: "8px",
              fontSize: "14px",
              backgroundColor: isDarkMode ? "#2d3748" : "#ffffff",
              color: isDarkMode ? "#e2e8f0" : "#495057",
            }}
          />
          <i
            className="fa fa-search"
            style={{
              position: "absolute",
              right: "16px",
              color: isDarkMode ? "#a0aec0" : "#6c757d",
              fontSize: "14px",
            }}
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              style={{
                position: "absolute",
                right: "40px",
                background: "transparent",
                border: "none",
                color: isDarkMode ? "#a0aec0" : "#6c757d",
                cursor: "pointer",
                padding: "4px",
              }}
            >
              <i className="fa fa-times" style={{ fontSize: "12px" }} />
            </button>
          )}
        </div>
      </div>

      <div
        style={{ height: "450px", overflowY: "auto", paddingRight: "8px" }}
        onScroll={handleScroll}
      >
        {loading && products.length === 0 ? (
          <div className="text-center py-5" style={{ color: isDarkMode ? "#a0aec0" : "#6c757d" }}>
            Loading products...
          </div>
        ) : products.length > 0 ? (
          <div>
            {products.map((product) => {
              const productKey = getProductId(product);
              return <RenderProductCard key={productKey} product={product} />;
            })}
            {loadingMore && (
              <div
                className="text-center py-3"
                style={{ color: isDarkMode ? "#a0aec0" : "#6c757d", fontSize: "13px" }}
              >
                Loading more...
              </div>
            )}
            {!hasMore && products.length > 0 && (
              <div
                className="text-center py-2"
                style={{ color: isDarkMode ? "#718096" : "#adb5bd", fontSize: "12px" }}
              >
                End of list
              </div>
            )}
          </div>
        ) : (
          <div
            className="text-center py-5"
            style={{
              background: isDarkMode ? "#2d3748" : "#f8f9fa",
              borderRadius: "12px",
              border: `2px dashed ${isDarkMode ? "#4a5568" : "#e9ecef"}`,
            }}
          >
            <p
              style={{
                color: isDarkMode ? "#a0aec0" : "#6c757d",
                fontSize: "13px",
                marginBottom: "6px",
              }}
            >
              No products found
              {searchTerm.length > 0 ? ` matching "${searchTerm}"` : ""}
            </p>
          </div>
        )}
      </div>
    </CustomModal>
  );
}
